import { useId, useState, type ReactNode } from 'react';
import { GLOSSARY } from '../intake/glossary.js';
import type { ReadinessStatus } from '../workspace/schema.js';
import { STATUS_LABELS } from '../readiness/rules.js';

/** Translucent fluid ribbons — decoration only, hidden from assistive tech. */
export function Ribbons({ variant = 'hero' }: { variant?: 'hero' | 'head' }) {
  const id = useId().replace(/:/g, '');
  return (
    <div className="ribbons" aria-hidden="true">
      <svg viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id={`${id}a`} x1="0" y1="0" x2="1" y2="0.4">
            <stop offset="0" stopColor="#22d3ee" stopOpacity="0" />
            <stop offset="0.35" stopColor="#22d3ee" stopOpacity="0.55" />
            <stop offset="0.7" stopColor="#2dd4bf" stopOpacity="0.45" />
            <stop offset="1" stopColor="#34d399" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`${id}b`} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#34d399" stopOpacity="0" />
            <stop offset="0.5" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="1" stopColor="#22d3ee" stopOpacity="0" />
          </linearGradient>
          <filter id={`${id}f`} x="-20%" y="-50%" width="140%" height="200%">
            <feGaussianBlur stdDeviation={variant === 'hero' ? 18 : 14} />
          </filter>
        </defs>
        <g filter={`url(#${id}f)`}>
          <path className="r r1" fill={`url(#${id}a)`} d="M-100 420 C 200 300, 420 520, 700 360 S 1150 160, 1320 260 L 1320 330 C 1150 240, 940 420, 700 450 S 230 400, -100 520 Z" />
          <path className="r r2" fill={`url(#${id}b)`} d="M-100 250 C 180 120, 460 300, 760 200 S 1100 40, 1320 110 L 1320 160 C 1110 110, 900 280, 740 280 S 220 220, -100 320 Z" />
          <path className="r r3" fill={`url(#${id}a)`} opacity="0.6" d="M-100 560 C 300 470, 600 600, 900 500 S 1200 420, 1320 450 L 1320 520 C 1100 520, 950 600, 760 600 S 200 600, -100 620 Z" />
        </g>
      </svg>
    </div>
  );
}

export function Logo({ size = 30 }: { size?: number }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#22d3ee" />
          <stop offset=".5" stopColor="#2dd4bf" />
          <stop offset="1" stopColor="#34d399" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="16" fill="var(--surface-2)" stroke="var(--line-strong)" />
      <path d="M10 42c10-14 22-20 44-22-14 6-24 14-30 28-5-2-10-4-14-6z" fill={`url(#${id})`} />
      <path d="M14 30c8-8 18-12 32-13-10 4-17 9-22 17-4-1-7-2-10-4z" fill={`url(#${id})`} opacity=".55" />
    </svg>
  );
}

export function PageHead({ eyebrow, title, children }: { eyebrow?: string; title: string; children?: ReactNode }) {
  return (
    <header className="page-head">
      <Ribbons variant="head" />
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h1 tabIndex={-1}>{title}</h1>
      {children && <p>{children}</p>}
    </header>
  );
}

/** A glossary term: click to reveal a plain-English explanation inline. */
export function Term({ id, children }: { id: string; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const entry = GLOSSARY[id];
  const popId = useId();
  if (!entry) return <>{children}</>;
  return (
    <>
      <button type="button" className="term" aria-expanded={open} aria-controls={popId} onClick={() => setOpen((o) => !o)}>
        {children ?? entry.term}
      </button>
      {open && <span id={popId} className="term-pop" role="note" style={{ display: 'block' }}><strong>{entry.term}.</strong> {entry.plain}</span>}
    </>
  );
}

export function StatusBadge({ status }: { status: ReadinessStatus }) {
  const cls = status === 'ready' ? 'good' : status === 'needs-review' ? 'warn' : '';
  return <span className={`badge ${cls}`}><span className="dot" />{STATUS_LABELS[status]}</span>;
}

export function Progress({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} className="progress">
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Money({ value, approx }: { value: number; approx?: boolean }) {
  const s = `${value < 0 ? '-' : ''}$${Math.round(Math.abs(value)).toLocaleString('en-US')}`;
  return <span className="num">{approx ? '~' : ''}{s}</span>;
}

export function Disclaimer({ compact }: { compact?: boolean }) {
  return (
    <p className={compact ? 'small faint' : 'small muted'}>
      <strong>Not a loan offer, credit decision or rate quote.</strong> Tyche Business is an independent project — not a
      lender, and not affiliated with the U.S. Small Business Administration. No application is taken here.
    </p>
  );
}
